import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import CloseIcon from '@mui/icons-material/Close'
import { Alert, IconButton } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'

import FadeAnimationDiv from 'components/common/Animations'
import scrollTo from 'hooks/scrollTo'
import { finishErrorAlert } from 'redux/alert/alert-actions'
import { RootState } from 'redux/store'
import { IMAGES_PATH, useTheme } from 'themes'

import { newFormThemeOverrides } from '../../styles'
import { IErrorAlertProps } from '../../types'

import AlertErrorCodes from './ErrorCodes'

const errorImg = IMAGES_PATH('/alert-error.svg')

const ErrorAlertComponent = ({ swapMargin = false, fullwidth = false }: IErrorAlertProps) => {
    const { formTheme } = useTheme({ formTheme: newFormThemeOverrides })
    const { t } = useTranslation(['override', 'translations'])
    const [open, setOpen] = useState(false)
    const dispatch = useDispatch()

    const { errorAlertText, errorAlert } = useSelector((state: RootState) => state.alert)

    useEffect(() => {
        if (errorAlert && errorAlertText?.some(Boolean)) {
            setOpen(true)
            scrollTo({ elementID: 'ErrorAlert', position: 'start' })
        }
    }, [errorAlert, errorAlertText])

    const handleClose = () => {
        setOpen(false)
        setTimeout(() => {
            dispatch(finishErrorAlert())
        }, 500)
    }

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={formTheme}>
                <div id="ErrorAlert" style={{ width: fullwidth ? '100%' : 'auto' }}>
                    <FadeAnimationDiv>
                        <Collapse in={open} timeout={500}>
                            {errorAlertText?.map(({ errorCode, message }) => (
                                <Alert
                                    key={errorCode || 'error'}
                                    icon={false}
                                    className={`error ${swapMargin ? 'swapMargin' : ''}`}
                                    action={
                                        <IconButton
                                            className="alertDismiss"
                                            aria-label="close"
                                            color="inherit"
                                            size="small"
                                            onClick={handleClose}
                                            data-sta-id="warning-alert-button-close"
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </IconButton>
                                    }
                                >
                                    <div className="iconBox">
                                        <img className="iconImg" src={errorImg} alt={t('Alert.Error')} />
                                    </div>
                                    <AlertErrorCodes errorCode={errorCode} message={message} />
                                </Alert>
                            ))}
                        </Collapse>
                    </FadeAnimationDiv>
                </div>
            </ThemeProvider>
        </StyledEngineProvider>
    )
}

export default ErrorAlertComponent
